import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CssBaseline,
  Link,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, createTheme, ThemeProvider } from '@mui/material/styles';
import TagManager from 'react-gtm-module';
import SecurityLogo from '../images/3eaf6a7b-7862-4992-8651-721d8e1b0e3e.png';
import PasswordResetShell, { PasswordResetLoader } from '../components/PasswordResetShell';
import { AppVarsContext } from '../context/AppVarsContext';
import { IMAGER_APP_ID, passwordLoginUrl } from '../password-links';

const normalizeSeverity = (value) => {
  if (['success', 'error', 'warning', 'info'].includes(value)) {
    return value;
  }

  if (value === 'ok') {
    return 'success';
  }

  if (typeof value === 'number') {
    return value >= 200 && value < 300 ? 'success' : 'error';
  }

  return 'info';
};

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setErrors] = useState({ show: false, msg: '', severity: 'info' });
  const [submitting, setSubmitting] = useState(false);
  const [requested, setRequested] = useState(false);
  const { appVars, appID } = useContext(AppVarsContext);

  useEffect(() => {
    TagManager.initialize({
      gtmId: 'GTM-PQ6FN6NX',
    });
  }, []);

  const theme = useMemo(() => {
    if (!appVars?.theme) {
      return createTheme();
    }

    try {
      return createTheme(JSON.parse(appVars.theme));
    } catch (parseError) {
      console.error('Unable to parse application theme:', parseError);
      return createTheme();
    }
  }, [appVars?.theme]);

  const handleClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setErrors({ show: false, msg: '', severity: 'info' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email.trim()) {
      setErrors({
        show: true,
        msg: 'Enter the email address tied to your account.',
        severity: 'error',
      });
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${appVars.api_url}/sendreset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          appID,
        }),
        redirect: 'follow',
      });

      const response = await res.json().catch(() => ({}));
      const severity = normalizeSeverity(response.status ?? res.status);
      if (res.ok) setRequested(true);

      setErrors({
        show: true,
        msg: response.msg || 'If the account exists, a reset email will be sent shortly.',
        severity,
      });
    } catch (err) {
      setErrors({ show: true, msg: err.message, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!appVars || !appVars.theme) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PasswordResetLoader message="Loading password recovery..." />
      </ThemeProvider>
    );
  }

  const logoSrc = appID === IMAGER_APP_ID ? 'https://imager.digitalwerks.com/public/brand/imager-logo.png' : `${appVars.api_url}/imageFiles/${appVars.appID}-logo.png`;
  const footerLabel = appVars.companyName || 'DigitalWerks';
  const loginHref = passwordLoginUrl(appID, appVars);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PasswordResetShell
        logoSrc={logoSrc}
        logoBackground={appID === IMAGER_APP_ID ? '#ffffff' : undefined}
        logoAlt={appVars.application}
        heroBadge="Secure account recovery"
        heroEyebrow={appVars.application}
        heroTitle={appID === IMAGER_APP_ID ? 'Get a fresh password link.' : 'Reset access without opening a support ticket.'}
        heroDescription={`Send a password reset link to the email address associated with your ${appVars.application} account. The secure link lets you set a new password and return to work quickly.`}
        heroHighlights={[
          {
            label: 'Delivery',
            value: 'The reset link is sent directly to the email address you provide.',
          },
          {
            label: 'Next step',
            value: 'Open the email, follow the secure link, and choose a new password.',
          },
        ]}
       // heroMediaSrc={heroImageSrc}
        heroMediaAlt={`${appVars.application} sign-in background`}
        heroMediaFallback="Use the newest password email to finish setting up your account or regain access."
        panelBadge="Password assistance"
        panelTitle="Enter your account email"
        panelDescription="Use the email tied to your login. If we find a matching account, we’ll send a secure reset link."
        footerHref={appVars.companyUrl || 'https://www.digitalwerks.com/'}
        footerLabel={footerLabel}
      >
        {requested ? <Alert severity="info" role="status" sx={{ mb: 3 }}>If an account matches that address, a password email is on its way. Check your inbox and spam folder. Use the newest link; if it expires, request another here.</Alert> : null}
        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Stack spacing={2.25}>
            <TextField
              fullWidth
              required
              id="email"
              label="Email address"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={submitting}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                },
              }}
            />
            <Paper
              elevation={0}
              sx={{
                p: 2.25,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              }}
            >
              <Stack spacing={1}>
                <Typography sx={{ fontWeight: 700 }}>How this works</Typography>
                <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                  After you submit your email, check your inbox for a password reset link. The message may take a minute to arrive, and it can land in spam or promotions folders.
                </Typography>
              </Stack>
            </Paper>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={submitting}
              startIcon={<Box component="img" src={SecurityLogo} alt="" sx={{ maxWidth: 28 }} />}
              sx={{
                py: 1.35,
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: 'none',
              }}
            >
              {submitting ? 'Sending secure link...' : 'Send Reset Link'}
            </Button>
            {loginHref ? (
              <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
                Remembered your password?{' '}
                <Link href={loginHref} underline="hover" sx={{ fontWeight: 700 }}>
                  Return to sign in
                </Link>
              </Typography>
            ) : null}
          </Stack>
        </Box>
      </PasswordResetShell>
      <Snackbar
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        open={error.show}
        autoHideDuration={4000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity={error.severity} variant="filled">
          {error.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default ResetPassword;
